import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChatPauseIcon, ChatPlayIcon } from '../../../assets/svg';
import { lightTheme } from '../../../styles/theme';
import { ConversationAttachmentEntity } from '../../../types/conversation.types';
import { formatDuration } from '../helpers/chatFormat';
import { useBarCount } from '../hooks/useBarCount';
import { createSoloPlayback } from '../hooks/soloPlayback';
import { usePointerSeek } from '../hooks/usePointerSeek';
import {
  BarsRow,
  PlayButton,
  Row,
  Time,
  Wave,
  WaveBar,
} from './ChatVoicePlayer.styled';
import { FLAT_PEAKS, loadVoiceNote, resamplePeaks } from './voiceWaveform';

/**
 * Voice messages, with the recording's own shape as the scrubber - the bars
 * fill as it plays, the way every messenger draws them.
 *
 * Not the shared `Audio` component: that one is a generic slider used by the
 * call-recording pages, and giving it a waveform would change them too. This
 * one is also the only place that already downloads the file (see
 * `loadVoiceNote`), so the peaks cost no extra request.
 */

/** A two-pixel bar plus the 1px of air `space-between` leaves beside it. */
const BAR_PITCH = 3;

const solo = createSoloPlayback();

export const ChatVoicePlayer = ({
  attachment,
}: {
  attachment: ConversationAttachmentEntity;
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  /* The scrubber's own element: it both measures the bars and receives the
     seek gesture, so the count and the pointer maths read the same box. */
  const { ref: waveRef, count: barCount } = useBarCount(BAR_PITCH);
  const fillRef = useRef<HTMLDivElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const [src, setSrc] = useState<string | null>(null);
  const [shape, setShape] = useState<number[]>(FLAT_PEAKS);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(
    attachment.durationMs ? attachment.durationMs / 1000 : 0
  );

  useEffect(() => {
    let cancelled = false;

    loadVoiceNote(attachment.url, attachment.mime, (objectUrl) => {
      if (cancelled) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      objectUrlRef.current = objectUrl;
      setSrc(objectUrl);
    })
      .then(({ peaks, duration: decoded }) => {
        if (cancelled) return;
        setShape(peaks);
        if (decoded) setDuration(decoded);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [attachment.url]);

  useEffect(() => {
    const element = audioRef.current;
    if (!element) return undefined;

    const onTime = () => setCurrentTime(element.currentTime || 0);
    const onMeta = () => {
      if (element.duration && Number.isFinite(element.duration)) {
        setDuration(element.duration);
      }
    };
    const onPlay = () => {
      solo.claim(element);
      setPlaying(true);
    };
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
      element.currentTime = 0;
      solo.release(element);
    };

    element.addEventListener('timeupdate', onTime);
    element.addEventListener('loadedmetadata', onMeta);
    element.addEventListener('durationchange', onMeta);
    element.addEventListener('play', onPlay);
    element.addEventListener('pause', onPause);
    element.addEventListener('ended', onEnded);

    return () => {
      element.removeEventListener('timeupdate', onTime);
      element.removeEventListener('loadedmetadata', onMeta);
      element.removeEventListener('durationchange', onMeta);
      element.removeEventListener('play', onPlay);
      element.removeEventListener('pause', onPause);
      element.removeEventListener('ended', onEnded);
      solo.release(element);
    };
  }, [src]);

  const bars = useMemo(
    () => resamplePeaks(shape, barCount),
    [shape, barCount]
  );

  /**
   * Painted imperatively rather than through state.
   *
   * The only thing that changes each frame is one clip-path. Routing that
   * through React would re-render every bar in the waveform sixty times a
   * second, for every voice note on screen, to move a single edge.
   */
  const paint = useCallback(
    (time: number) => {
      const element = fillRef.current;
      if (!element) return;
      const played = duration ? Math.min(1, Math.max(0, time / duration)) : 0;
      element.style.clipPath = `inset(0 ${100 - played * 100}% 0 0)`;
    },
    [duration]
  );

  /* Covers the paused cases - a seek, the reset on end, and first paint. */
  useLayoutEffect(() => {
    paint(currentTime);
  }, [paint, currentTime, bars]);

  /**
   * `timeupdate` fires about four times a second, which is what made the fill
   * advance in visible steps. While the clip is actually moving it is driven
   * off the frame clock instead; the event still covers everything else.
   *
   * The seconds counter beside it stays on state, but only re-renders when the
   * whole second changes - it has nothing finer to show.
   */
  useEffect(() => {
    if (!playing) return undefined;
    let frame = 0;

    const tick = () => {
      const element = audioRef.current;
      if (element) {
        const time = element.currentTime || 0;
        paint(time);
        setCurrentTime((previous) =>
          Math.floor(previous) === Math.floor(time) ? previous : time
        );
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, paint]);

  const togglePlay = () => {
    const element = audioRef.current;
    if (!element || !src) return;
    if (element.paused) element.play().catch(() => undefined);
    else element.pause();
  };

  const { seekHandlers } = usePointerSeek({
    trackRef: waveRef,
    duration,
    onSeek: (seconds) => {
      const element = audioRef.current;
      if (!element) return;
      element.currentTime = seconds;
      setCurrentTime(seconds);
    },
  });

  return (
    <Row>
      <audio ref={audioRef} src={src ?? undefined} preload="auto" />

      <PlayButton
        type="button"
        disabled={!src || failed}
        aria-label={playing ? 'Pause' : 'Play'}
        onClick={togglePlay}
      >
        {/* White on both, explicitly - this button is red. The same pair sits
            on the video player's white disc, where they have to be red. */}
        {playing ? (
          <ChatPauseIcon color={lightTheme.white} />
        ) : (
          <ChatPlayIcon color={lightTheme.white} />
        )}
      </PlayButton>

      <Wave
        ref={waveRef}
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={duration || 0}
        aria-valuenow={currentTime}
        {...seekHandlers}
      >
        <BarsRow aria-hidden>
          {bars.map((peak, index) => (
            <WaveBar key={index} $peak={peak} />
          ))}
        </BarsRow>

        {/* No `style` prop on purpose: `paint` owns this element's clip-path,
            and a style React manages would be rewritten on every render. */}
        <BarsRow ref={fillRef} aria-hidden>
          {bars.map((peak, index) => (
            <WaveBar key={index} $peak={peak} $played />
          ))}
        </BarsRow>
      </Wave>

      <Time>
        {formatDuration(
          (currentTime > 0 || playing ? currentTime : duration) * 1000
        )}
      </Time>
    </Row>
  );
};
